import React from 'react';
import DOMPurify from 'dompurify';
import { Box, Typography, Button } from '@mui/material';

const Section = ({ component, header, text, image, imageOnLeft, button, path }) => {
    const sanitizedText = text
      ? DOMPurify.sanitize(text, {
          ADD_ATTR: ['target', 'rel', 'aria-label']
        })
          .replace(/&nbsp;/g, ' ')
          .replace(/\s{2,}/g, ' ')
      : '';

    const isExternal = typeof path === 'string' && /^https?:\/\//i.test(path);

    if (component) {
        const Component = component;

        return (
            <Box
                display="flex"
                flexWrap="wrap"
                sx={{
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    maxWidth: '100%',
                    width: '100%',
                    margin: '0 auto',
                    overflow: 'hidden',
                    overflowX: 'hidden',
                    bgcolor: image ? 'background.paper' : 'transparent',
                    borderRadius: 4,
                    px: { xs: 1.5, md: 2 },
                    gap: { xs: 1, md: 1.5 },
                    py: { xs: 1, md: 2 },
                    minWidth: 0,
                }}
            >
                {image && (
                  <Box
                    component="img"
                    src={image.src}
                    alt={header}
                    sx={{
                      width: { xs: '60%', sm: '50%', md: image.width || '150px' },
                      maxWidth: { xs: 220, md: image.width || 150 },
                      height: { xs: 'auto', md: image.height || '150px' },
                      borderRadius: '8px',
                      objectFit: 'contain',
                      display: 'block',
                      mx: 'auto',
                    }}
                  />
                )}

                {header && (
                    <Typography
                        variant="h3"
                        gutterBottom
                        sx={{
                            color: 'primary.main',
                            fontFamily: 'Arial, cursive',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            fontSize: { xs: '1.4rem', sm: '1.55rem', md: '1.8rem' },
                            lineHeight: { xs: 1.2, md: 1.2 },
                            wordBreak: 'break-word',
                            mb: { xs: 0.5, md: 1 },
                        }}
                    >
                        {header}
                    </Typography>
                )}

                <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
                    <Component />
                </Box>
            </Box>
        );
    }

    return (
        <Box
            display="flex"
            flexWrap="wrap"
            sx={{
                flexDirection: { xs: 'column', md: image ? (imageOnLeft ? 'row' : 'row-reverse') : 'column' },
                alignItems: 'center',
                justifyContent: { xs: 'center', md: 'flex-start' },
                maxWidth: '900px',
                margin: '0 auto',
                overflow: 'hidden',
                bgcolor: image ? 'background.paper' : 'transparent',
                borderRadius: 4,
                px: { xs: 1.5, md: 2 },
                gap: { xs: 1, md: 2 },
                py: { xs: 1, md: 2 },
            }}
        >
            {image && (
              <Box
                component="img"
                src={image.src}
                alt={header}
                sx={{
                  width: { xs: '60%', sm: '50%', md: image.width || '150px' },
                  maxWidth: { xs: 220, md: image.width || 150 },
                  height: { xs: 'auto', md: image.height || '150px' },
                  borderRadius: '8px',
                  objectFit: 'contain',
                  display: 'block',
                  mx: 'auto',
                }}
              />
            )}
            <Box
                flex="1"
                sx={{
                    textAlign: { xs: 'center', md: image ? 'left' : 'center' },
                    maxWidth: '100%',
                    p: 2,
                }}
            >
                {header && (
                    <Typography
                        variant="h3"
                        gutterBottom
                        sx={{
                            color: 'primary.main',
                            fontFamily: 'Arial, cursive',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            fontSize: { xs: '1.4rem', sm: '1.55rem', md: '1.8rem' },
                            lineHeight: { xs: 1.2, md: 1.2 },
                            wordBreak: 'break-word',
                            mb: { xs: 0.5, md: 1 },
                        }}
                    >
                        {header}
                    </Typography>
                )}
                {sanitizedText && (
                  <Typography
                    variant="body1"
                    lang="uk"
                    sx={{
                      color: 'text.secondary',
                      textAlign: { xs: 'left', sm: 'left', md: 'justify' },
                      textJustify: 'inter-word',
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      lineHeight: { xs: 1.45, md: 1.5 },
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      hyphens: 'auto',
                      width: '100%',
                      '& a': { wordBreak: 'break-all' },
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizedText }}
                  />
                )}
                {button && (
                    <Box textAlign={{ xs: 'center', md: image ? 'left' : 'center' }} mt={{ xs: 1, md: 2 }}>
                        <Button
                            variant="outlined"
                            href={path || undefined}
                            target={isExternal ? '_blank' : undefined}
                            rel={isExternal ? 'noopener noreferrer' : undefined}
                            sx={{
                                bgcolor: '#356929',
                                color: 'white',
                                fontSize: '0.95rem',
                                padding: '8px 14px',
                                borderRadius: '8px',
                                textDecoration: 'none',
                                '&:hover': {
                                    bgcolor: '#356949',
                                },
                            }}
                        >
                            {button}
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default Section;
